import type { TenantTheme } from '../api/types'
import { isHexColor } from './contrast'
import { allThemeProperties, isColorToken, TOKEN_PREFIX } from './tokens'

/*
  Applies a ranch's theme to the live document. Tracer's web/src/theme/applyTheme.ts,
  reduced to the eight colour tokens a ranch may set (brief §2.8).

  WHY THE DOM API AND NOT AN INJECTED <style>

  Every value here was typed by a ranch owner, and the two ways to get a custom property
  onto the page are not equally safe. Building a stylesheet string and injecting it means
  a value containing `; }` closes the rule and opens a new one, and the theme becomes
  stored CSS injection against every user in the organization. `setProperty` cannot do
  that: the browser parses the value against the property's grammar and drops it if it
  does not fit. The value is also checked to be a hex colour before it is written, so
  `url(...)` never reaches the document at all.

  WHY EVERY CHANGE IS A FULL REVERT AND REAPPLY

  Switching ranches is the normal case; the switcher is in the header. If applying a
  theme only ever set properties, a ranch that overrides eight colours followed by one
  that overrides three would leave five of the first ranch's colours on the page. So the
  exact set of properties this module wrote is remembered, and removed before the next
  set is written.
*/

/** The custom properties this module has written, so it can take them all back. */
let appliedProperties: string[] = []

/** The last theme applied, so it can be re-asserted. See {@link reapplyTheme}. */
let lastApplied: TenantTheme | null = null

/**
 * The values the stylesheet declares, captured before any theme is written.
 *
 * Custom properties inherit. The theme is written onto `documentElement`, so by the time
 * the brand editor asks "what is the default accent?", every element in the document has
 * inherited the RANCH's accent. Reading once, before the first theme is applied, is the
 * only reliable way to keep the defaults.
 */
let defaultTokens: Record<string, string> | null = null

const listeners = new Set<() => void>()

/** Identity of a theme by its bytes, so a refetch that changes nothing repaints nothing. */
export function themeFingerprint(theme: TenantTheme | null | undefined): string {
  if (!theme) return ''
  return JSON.stringify({
    o: theme.organizationId,
    c: theme.colors,
    n: theme.ranchDisplayName,
    l: theme.logoLightUrl,
    d: theme.logoDarkUrl,
  })
}

/**
 * Writes a theme onto the document, replacing whatever the last call wrote.
 *
 * Safe to call with null, which is how a ranch goes back to the platform brand and how
 * the very first render behaves before the theme has loaded.
 */
export function applyTheme(theme: TenantTheme | null | undefined): void {
  const root = document.documentElement
  lastApplied = theme ?? null

  if (defaultTokens === null) {
    const computed = getComputedStyle(root)
    const captured: Record<string, string> = {}
    for (const property of allThemeProperties()) {
      captured[property] = computed.getPropertyValue(property).trim()
    }
    defaultTokens = captured
  }

  for (const property of appliedProperties) {
    root.style.removeProperty(property)
  }
  appliedProperties = []

  if (theme) {
    writeColors(theme.colors)
  }

  for (const listener of listeners) listener()
}

/**
 * Paints a draft palette over the live document without forgetting the saved theme:
 * the brand editor's live preview. {@link reapplyTheme} takes it off again.
 */
export function previewColors(colors: Record<string, string>): void {
  const root = document.documentElement
  for (const property of appliedProperties) {
    root.style.removeProperty(property)
  }
  appliedProperties = []
  writeColors(colors)
  for (const listener of listeners) listener()
}

function writeColors(colors: Record<string, string> | null | undefined): void {
  if (!colors) return
  const root = document.documentElement
  for (const [token, value] of Object.entries(colors)) {
    /*
      The allow-list is enforced again here, on the client. Not because the API's check
      is doubted, but because this is the last point before a name from a JSON document
      becomes a property name on the live document.
    */
    if (!isColorToken(token)) continue
    if (typeof value !== 'string' || !isHexColor(value)) continue
    const property = `${TOKEN_PREFIX.colors}${token}`
    root.style.setProperty(property, value.trim())
    appliedProperties.push(property)
  }
}

/** Re-asserts the saved theme after a preview has written over these properties. */
export function reapplyTheme(): void {
  applyTheme(lastApplied)
}

/** The theme currently on the document, or null for the platform brand. */
export function currentTheme(): TenantTheme | null {
  return lastApplied
}

/** Told after every theme change, for the few things that paint to a canvas. */
export function onThemeApplied(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** What the stylesheet declares for each token: the editor's default swatches and reset. */
export function getDefaultTokens(): Record<string, string> {
  return defaultTokens ?? {}
}

/** The properties written by the last apply, for tests and the editor. */
export function appliedThemeProperties(): readonly string[] {
  return appliedProperties
}

/** Test seam: forget what the document shipped with. */
export function resetThemeForTests(): void {
  for (const property of appliedProperties) {
    document.documentElement.style.removeProperty(property)
  }
  appliedProperties = []
  defaultTokens = null
  lastApplied = null
  listeners.clear()
}
